<template>
    <div class="vue-apexcharts" />
</template>
<script setup lang="ts">
import ApexCharts from 'apexcharts'

const events = [
  'animationEnd',
  'beforeMount',
  'mounted',
  'updated',
  'click',
  'mouseMove',
  'mouseLeave',
  'legendClick',
  'markerClick',
  'selection',
  'dataPointSelection',
  'dataPointMouseEnter',
  'dataPointMouseLeave',
  'beforeZoom',
  'beforeResetZoom',
  'zoomed',
  'scrolled',
  'brushScrolled'
]

const props = defineProps({
  options: {
    type: Object,
    default: () => ({})
  },
  type: {
    type: String,
    default: ''
  },
  series: {
    type: Array,
    required: true
  },
  width: {
    type: String,
    default: '100%'
  },
  height: {
    type: String,
    default: 'auto'
  }
})

const emits = defineEmits([
  'animationEnd',
  'beforeMount',
  'mounted',
  'updated',
  'click',
  'mouseMove',
  'mouseLeave',
  'legendClick',
  'markerClick',
  'selection',
  'dataPointSelection',
  'dataPointMouseEnter',
  'dataPointMouseLeave',
  'beforeZoom',
  'beforeResetZoom',
  'zoomed',
  'scrolled',
  'brushScrolled'
])

const __el = ref(null)
const chart: any = ref(null)

const isObject = (item: any) => {
  return item && typeof item === 'object' && !Array.isArray(item) && item != null
}

const extend = (target: any, source: any) => {
  if (typeof Object.assign !== 'function') {
    ;(function () {
      Object.assign = function (target: any) {
        // We must check against these specific cases.
        if (target === undefined || target === null) {
          throw new TypeError('Cannot convert undefined or null to object')
        }

        const output = Object(target)
        for (let index = 1; index < arguments.length; index++) {
          const source = arguments[index]
          if (source !== undefined && source !== null) {
            for (const nextKey in source) {
              if (source[nextKey]) {
                output[nextKey] = source[nextKey]
              }
            }
          }
        }
        return output
      }
    })()
  }

  const output = Object.assign({}, target)
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, {
            [key]: source[key]
          })
        } else {
          output[key] = extend(target[key], source[key])
        }
      } else {
        Object.assign(output, {
          [key]: source[key]
        })
      }
    })
  }
  return output
}

const init = async () => {
  await nextTick()

  if (chart.value) {
    return
  }

  const newOptions = {
    chart: {
      type: props.type || props.options?.chart.type || 'line',
      height: props.height,
      width: props.width,
      events: {} as any
    },
    series: props.series
  }

  events.forEach((event) => {
    const callback = (...args: any[]) => emits(event as keyof typeof emits, ...args)
    newOptions.chart.events[event] = callback
  })

  const config = extend(props.options, newOptions)

  chart.value = new ApexCharts(__el.value, config)
  return chart.value.render()
}

const refresh = () => {
  destroy()
  return init()
}

const destroy = () => {
  chart.value.destroy()
}

const updateSeries = (newSeries: any, animate: any) => {
  return chart.value.updateSeries(newSeries, animate)
}

const updateOptions = (newOptions: any, redrawPaths: any, animate: any, updateSyncedCharts: any) => {
  return chart.value.updateOptions(newOptions, redrawPaths, animate, updateSyncedCharts)
}

const toggleSeries = (seriesName: any) => {
  return chart.value.toggleSeries(seriesName)
}

const showSeries = (seriesName: any) => {
  chart.value.showSeries(seriesName)
}

const hideSeries = (seriesName: any) => {
  chart.value.hideSeries(seriesName)
}

const appendSeries = (newSeries: any, animate: any) => {
  return chart.value.appendSeries(newSeries, animate)
}

const resetSeries = () => {
  chart.value.resetSeries()
}

const toggleDataPointSelection = (seriesIndex: any, dataPointIndex: any) => {
  chart.value.toggleDataPointSelection(seriesIndex, dataPointIndex)
}

const appendData = (newData: any) => {
  return chart.value.appendData(newData)
}

const zoomX = (start: any, end: any) => {
  return chart.value.zoomX(start, end)
}

const dataURI = (options: any) => {
  return chart.value.dataURI(options)
}

const setLocale = (localeName: any) => {
  return chart.value.setLocale(localeName)
}

const addXaxisAnnotation = (options: any, pushToMemory: any) => {
  chart.value.addXaxisAnnotation(options, pushToMemory)
}

const addYaxisAnnotation = (options: any, pushToMemory: any) => {
  chart.value.addYaxisAnnotation(options, pushToMemory)
}

const addPointAnnotation = (options: any, pushToMemory: any) => {
  chart.value.addPointAnnotation(options, pushToMemory)
}

const removeAnnotation = (id: any, options: any) => {
  chart.value.removeAnnotation(id, options)
}

const clearAnnotations = () => {
  chart.value.clearAnnotations()
}

onBeforeMount(() => {
  if (process.client) {
    // window.ApexCharts = ApexCharts
  }
})

onMounted(() => {
  __el.value = getCurrentInstance()?.proxy?.$el
  init()
})

onBeforeUnmount(() => {
  if (!chart.value) {
    return
  }
  destroy()
})

const reactiveProps = toRefs(props)
watch(reactiveProps.options, () => {
  if (!chart.value && props.options) {
    init()
  } else {
    chart.value.updateOptions(props.options)
  }
})

watch(
  reactiveProps.series,
  () => {
    if (!chart.value && props.series) {
      init()
    } else {
      chart.value.updateSeries(props.series)
    }
  },
  { deep: true }
)

watch(reactiveProps.type, () => {
  refresh()
})

watch(reactiveProps.width, () => {
  refresh()
})

watch(reactiveProps.height, () => {
  refresh()
})
</script>
